// /welcome: signed in, but not in a company (WEB_PLAN.md §5.2). Port of GroundTeam/Views/Sync/NoCompanyView.swift as
// the iOS gate shows it ("Your company"): who is signed in, invitations sent to this account's confirmed email, joining
// with an invite code, creating a company (the account becomes its admin), and signing out or deleting the account.
// Nothing is joined without an explicit click, and an invitation from the list is confirmed first.
//
// Not under SyncProvider: there is no company to sync yet. Joining or creating ends with `refreshMemberships()`; the
// route guard then opens the console. A new company is opened only once its seed (settings, the admin's profile,
// pipelines, visit results…) is on the server, since a company without settings sends iPhones to setup.
// Sample data is an iPhone feature and is not offered here.

import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { accountLabel } from '@/auth/accountText';
import { useAuth } from '@/auth/AuthProvider';
import { isSessionFailure } from '@/auth/errors';
import { companyDisplayName, decideMembership, fetchMemberships } from '@/auth/memberships';
import { sharedProfileName } from '@/auth/pending';
import type { InvitationOffer } from '@/contracts/wire';
import { useDocumentTitle } from '@/features/common/useDocumentTitle';
import { ConfirmDialog, ThemeSwitcher } from '@/ui';
import { useLatest } from '@/ui/hooks';
import { acceptInvitation, fetchInvitationOffers, previewInviteCode } from './api';
import { accountProfileName } from './companySetup';
import { AccountActions, AccountHeader } from './components/AccountSections';
import { CreateCompanySection } from './components/CreateCompanySection';
import { InvitationsSection } from './components/InvitationsSection';
import { InviteCodeSection } from './components/InviteCodeSection';
import { ProgressPanel } from './components/ProgressPanel';
import { isCodeComplete, readInviteLink } from './inviteCode';
import { takeStashedInviteCode } from './inviteLinkStash';
import { joinConfirmTitle, joinDetail, offerCompany } from './offers';
import { clearStalePendingSetup } from './setupRunner';
import { useCompanySetup } from './useCompanySetup';
import {
  codeErrorMessage,
  createdTitle,
  FINISHING_TITLE,
  finishingDetail,
  invitationCheckErrorMessage,
  joinedTitle,
  joinErrorMessage,
  openFailedText,
  PAGE_TITLE,
} from './welcomeText';

function isOnline(): boolean {
  return typeof navigator === 'undefined' || navigator.onLine !== false;
}

/** Joined, created, or found already in a company: memberships are being re-read so the console opens. */
interface Opening {
  companyName: string;
  /** "You joined X" after a join; "X is ready" after a create or when "Check again" found the company. */
  kind: 'joined' | 'ready';
  /** The membership check is running. */
  checking: boolean;
  /** A check finished and the console still isn't open (it would have replaced this page). */
  attempted: boolean;
}

export default function WelcomePage() {
  useDocumentTitle(PAGE_TITLE);
  const { user, error: authError, refreshMemberships, signOut } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const userId = user?.id ?? '';
  const email = user?.email?.trim() || null;
  const account = accountLabel(user);
  // Apple's shared name or the provider's name claims, read once (typing must not be overwritten).
  const [initialName] = useState(() => accountProfileName(user?.user_metadata, user ? sharedProfileName(user.id) : null));

  // After joining or creating
  const [opening, setOpening] = useState<Opening | null>(null);
  const openCompany = useCallback(async (companyName: string, kind: Opening['kind']) => {
    setOpening({ companyName, kind, checking: true, attempted: false });
    await refreshMemberships();
    // Still mounted means the console didn't open: the check failed (authError says why) or found nothing yet.
    setOpening((o) => (o ? { ...o, checking: false, attempted: true } : o));
  }, [refreshMemberships]);

  const setup = useCompanySetup(user, (companyName) => {
    void openCompany(companyName, 'ready');
  });
  const setupPhase = setup.state.phase;
  const settingUp = setupPhase === 'finishing' || setupPhase === 'ready';

  // Invitations for you (TanStack Query: refreshed when the tab regains focus, like iOS when the app becomes active)
  const invitations = useQuery({
    queryKey: ['welcome', 'my_invitations', userId],
    queryFn: fetchInvitationOffers,
    enabled: userId !== '' && opening === null && !settingUp,
    // Who invited this account is nobody else's business: the list leaves the cache with the page (the query client
    // outlives sign-outs).
    gcTime: 0,
  });
  const [checkingAgain, setCheckingAgain] = useState(false);
  const [checkedOnce, setCheckedOnce] = useState(false);
  const [checkError, setCheckError] = useState<string | null>(null);
  const [offersError, setOffersError] = useState<string | null>(null);
  const [confirmOffer, setConfirmOffer] = useState<InvitationOffer | null>(null);
  const [joiningId, setJoiningId] = useState<string | null>(null);

  // Invite code
  const [code, setCode] = useState('');
  const [codeOffer, setCodeOffer] = useState<InvitationOffer | null>(null);
  const [codeError, setCodeError] = useState<string | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const codeInput = useRef<HTMLInputElement | null>(null);
  const focusCodeNext = useRef(false);

  // Account
  const [signingOut, setSigningOut] = useState(false);
  const [deleting, setDeleting] = useState(false);

  /** Anything that changes the account or company is running (checking for invitations doesn't block the rest). */
  const isWorking = previewing || joiningId !== null || setupPhase === 'creating'
    || (setup.state.phase === 'finishing' && setup.state.retrying) || signingOut || deleting || (opening?.checking ?? false);
  /** The company sections are hidden (a panel shows) or busy: an invite link arriving now is not acted on. */
  const linkBlocked = useLatest(isWorking || opening !== null || settingUp);

  const preview = useCallback(async (typed: string) => {
    if (!isCodeComplete(typed)) return;
    setPreviewing(true);
    setCodeError(null);
    try {
      setCodeOffer(await previewInviteCode(typed));
    } catch (e) {
      if (!isSessionFailure(e)) setCodeError(codeErrorMessage(e));
    } finally {
      setPreviewing(false);
    }
  }, []);

  // Once per visit: a stored setup for a company this account is no longer in is dropped (with a company it would be
  // in the console, not here), and the invite code stashed across sign-in is taken (read once, so an old code never
  // fills the field later). Then, on arrival and whenever an invite link is opened in this tab, its #code=… fills the
  // field. The code works like a password, so it leaves the address bar right away; a complete one is checked at once
  // (joining still takes a click).
  const visitHandled = useRef(false);
  useEffect(() => {
    if (userId === '') return;
    let linked: string | null = null;
    if (!visitHandled.current) {
      visitHandled.current = true;
      clearStalePendingSetup(userId);
      linked = takeStashedInviteCode();
    }
    const link = readInviteLink(location.hash);
    if (link.code !== null) {
      void navigate({ pathname: location.pathname, search: location.search, hash: link.hash }, { replace: true });
      linked = link.code;
    }
    if (!linked || linkBlocked.current) return;
    setCodeOffer(null);
    setCodeError(null);
    setCode(linked);
    if (isCodeComplete(linked)) void preview(linked);
  }, [linkBlocked, location.hash, location.pathname, location.search, navigate, preview, userId]);

  useEffect(() => {
    if (codeOffer === null && focusCodeNext.current) {
      focusCodeNext.current = false;
      codeInput.current?.focus();
    }
  }, [codeOffer]);

  /** Accepts on the server, then opens the company. `code` is set when joining with an invite code. */
  const accept = async (offer: InvitationOffer, withCode: string | null) => {
    if (isWorking) return;
    setJoiningId(offer.id);
    setOffersError(null);
    setCodeError(null);
    const company = offerCompany(offer);
    try {
      const membership = await acceptInvitation(offer, withCode);
      setJoiningId(null);
      void openCompany(membership.company_name?.trim() || company, 'joined');
    } catch (e) {
      setJoiningId(null);
      if (isSessionFailure(e)) return;
      const message = joinErrorMessage(e, company);
      if (withCode === null) {
        setOffersError(message);
        // Expired or withdrawn meanwhile: show the current list.
        void invitations.refetch();
      } else {
        setCodeError(message);
      }
    }
  };

  /**
   * "Check again" (SyncCenter.checkInvitations): a company this account is in meanwhile (joined or created on another
   * device, or a join whose answer got lost) opens like after a join, with its own Try again if that check fails;
   * otherwise the invitations are read again. Offline or server trouble shows as an error, not as "Still nothing".
   */
  const checkAgain = async () => {
    if (checkingAgain || isWorking) return;
    setCheckingAgain(true);
    setOffersError(null);
    setCheckError(null);
    try {
      const decision = decideMembership(await fetchMemberships());
      if (decision.status === 'ready') {
        void openCompany(companyDisplayName(decision.membership), 'ready');
        return;
      }
      if (decision.status === 'suspended') {
        // The route guard shows "Your access was paused".
        await refreshMemberships();
        return;
      }
      await invitations.refetch();
    } catch (e) {
      if (!isSessionFailure(e)) setCheckError(invitationCheckErrorMessage(e, isOnline()));
    } finally {
      setCheckingAgain(false);
      setCheckedOnce(true);
    }
  };

  const signOutForAnotherAccount = async () => {
    if (isWorking) return;
    setSigningOut(true);
    try {
      await signOut();
    } finally {
      setSigningOut(false);
    }
  };

  const offers = invitations.data ?? [];
  const queryError = invitations.error && !isSessionFailure(invitations.error)
    ? invitationCheckErrorMessage(invitations.error, isOnline())
    : null;
  const listError = offersError ?? checkError ?? queryError;

  let panel: ReactNode = null;
  if (opening) {
    const name = opening.companyName;
    panel = (
      <ProgressPanel
        kind="opening"
        title={opening.kind === 'joined' ? joinedTitle(name) : createdTitle(name)}
        opening={opening.checking}
        problem={opening.attempted ? (authError ?? openFailedText(name)) : null}
        onRetry={() => void openCompany(name, opening.kind)}
      />
    );
  } else if (setup.state.phase === 'finishing') {
    const { problem, retrying } = setup.state;
    panel = (
      <ProgressPanel
        kind="finishing"
        title={FINISHING_TITLE}
        detail={finishingDetail(setup.state.companyName)}
        // While another try is coming it says so; once it's the person's turn, the wording points at Try again.
        problem={problem ? (retrying ? problem.retryingText : problem.text) : null}
        retrying={retrying}
        onRetry={setup.retry}
      />
    );
  } else if (setup.state.phase === 'ready') {
    panel = <ProgressPanel kind="opening" title={createdTitle(setup.state.companyName)} opening problem={null} onRetry={() => {}} />;
  }

  return (
    <div className="relative min-h-dvh bg-canvas text-fg">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 h-80 bg-gradient-to-b from-navy/10 via-orange/5 to-transparent dark:from-navy/45 dark:via-transparent"
      />
      <main className="relative mx-auto flex w-full max-w-5xl flex-col gap-6 px-4 py-8 sm:px-6 lg:py-12">
        <div className="flex items-center gap-2">
          <span aria-hidden className="flex size-8 items-center justify-center rounded-lg bg-navy text-sm font-black text-white">
            GT
          </span>
          <span className="flex-1 text-lg font-extrabold tracking-tight whitespace-nowrap text-fg">Ground Team</span>
          <ThemeSwitcher variant="icons" />
        </div>
        <header>
          <h1 className="text-2xl font-bold tracking-tight text-fg sm:text-3xl">{PAGE_TITLE}</h1>
          <p className="mt-1 max-w-2xl text-sm text-fg-muted">
            Join your team with an invitation or an invite code, or create your company and invite your team.
          </p>
        </header>

        <div className={panel ? 'flex w-full max-w-2xl flex-col gap-6' : 'flex flex-col gap-6'}>
          <AccountHeader account={account} />
          {panel ?? (
            <div className="grid items-start gap-6 lg:grid-cols-2">
              <div className="flex min-w-0 flex-col gap-6">
                <InvitationsSection
                  offers={offers}
                  loading={invitations.isPending}
                  checking={checkingAgain || (invitations.isFetching && !invitations.isPending)}
                  checkedOnce={checkedOnce}
                  error={listError}
                  email={email}
                  joiningId={joiningId}
                  disabled={isWorking}
                  onJoin={(offer) => setConfirmOffer(offer)}
                  onCheckAgain={() => void checkAgain()}
                />
                <InviteCodeSection
                  code={code}
                  onCodeChange={(next) => {
                    setCode(next);
                    if (codeError) setCodeError(null);
                  }}
                  offer={codeOffer}
                  previewing={previewing}
                  joining={codeOffer !== null && joiningId === codeOffer.id}
                  error={codeError}
                  disabled={isWorking}
                  onPreview={() => void preview(code)}
                  onJoin={(offer) => void accept(offer, code)}
                  onClear={() => {
                    focusCodeNext.current = true;
                    setCodeOffer(null);
                    setCode('');
                    setCodeError(null);
                  }}
                  inputRef={codeInput}
                />
              </div>
              <CreateCompanySection
                initialName={initialName}
                creating={setupPhase === 'creating'}
                error={setup.error}
                disabled={isWorking}
                onCreate={(input) => void setup.create(input)}
              />
            </div>
          )}
          <AccountActions
            account={account}
            disabled={isWorking}
            signingOut={signingOut}
            onSignOut={() => void signOutForAnotherAccount()}
            onDeletingChange={setDeleting}
          />
        </div>
      </main>

      <ConfirmDialog
        open={confirmOffer !== null}
        onClose={() => setConfirmOffer(null)}
        onConfirm={() => {
          const offer = confirmOffer;
          if (offer) void accept(offer, null);
        }}
        title={confirmOffer ? joinConfirmTitle(confirmOffer) : 'Join this company?'}
        message={confirmOffer ? joinDetail(confirmOffer) : undefined}
        confirmLabel={confirmOffer ? `Join ${offerCompany(confirmOffer)}` : 'Join'}
      />
    </div>
  );
}
